
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import Button from '../components/Button';
import Card from '../components/Card';
import Alert from '../components/Alert';
import { format } from 'date-fns';
import { trackEvent } from '../config/analytics';

// Safe date formatter that handles Firestore timestamps, ISO strings, and invalid values
const safeFormatDate = (dateValue, formatStr = 'MMMM d, yyyy') => {
  try {
    if (!dateValue) return null;

    let date;
    if (dateValue?.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;

    return format(date, formatStr);
  } catch (err) {
    console.error('Date formatting error:', err);
    return null;
  }
};

const SubscriptionPage = () => {
  const { userData } = useAuth();
  const { subscribeToPlan, manageBilling, loading } = useSubscription();
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan;

  useEffect(() => {
    trackEvent('subscription_page_viewed', { 
      currentPlan: userData?.subscriptionTier,
      status: userData?.subscriptionStatus 
    });
  }, [userData]);

  const pricingPlans = [
    {
      name: 'Small Business',
      price: 97,
      pages: 10,
      frequency: '1 audit/week',
      description: 'Perfect for small sites with occasional updates',
      features: [
        'Weekly audits',
        'Up to 10 pages per audit',
        'Email reports',
        'WCAG 2.2 AA testing',
        'Basic support',
      ],
      priceId: process.env.REACT_APP_STRIPE_PRICE_SMALL,
      tierId: 'small',
    },
    {
      name: 'Standard',
      price: 297,
      pages: 100,
      frequency: '3 audits/week',
      description: 'Ideal for growing businesses',
      features: [
        '3 audits per week',
        'Up to 100 pages per audit',
        'Priority support',
        'Change detection',
        'Issue tracking',
        'API access',
      ],
      priceId: process.env.REACT_APP_STRIPE_PRICE_STANDARD,
      tierId: 'standard',
      popular: true,
    },
    {
      name: 'Full Enterprise',
      price: 797,
      pages: 1000,
      frequency: '1 audit/day',
      description: 'For high-volume commerce sites',
      features: [
        'Daily audits',
        'Up to 1000 pages per audit',
        'Dedicated support',
        'Custom reporting',
        'Slack integration',
        'White-label reports',
        'Priority processing',
      ],
      priceId: process.env.REACT_APP_STRIPE_PRICE_FULL,
      tierId: 'full',
    },
  ];

  const handleSubscribe = async (plan) => {
    try {
      trackEvent('subscription_plan_selected', { tier: plan.tierId, price: plan.price });
      await subscribeToPlan(plan.priceId, plan.tierId);
    } catch (err) {
      console.error('Subscription error:', err);
    }
  };

  const handleManageBilling = async () => {
    try {
      await manageBilling();
    } catch (err) {
      console.error('Billing portal error:', err);
    }
  };

  const getTierName = (tier) => {
    const names = {
      small: 'Small Business',
      standard: 'Standard',
      full: 'Full Enterprise',
    };
    return names[tier] || 'No Plan';
  };

  const isCurrentPlan = (tierId) => {
    return userData?.subscriptionTier === tierId && userData?.subscriptionStatus === 'active';
  };

  const hasActiveSubscription = userData?.subscriptionStatus === 'active';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
          <p className="mt-2 text-gray-600">Choose the plan that fits your needs</p>
        </div>

        {/* Current Subscription */}
        {hasActiveSubscription && (
          <Card className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Plan</h3>
                <p className="text-2xl font-bold text-indigo-600 mb-1">
                  {getTierName(userData.subscriptionTier)}
                </p>
                <p className="text-sm text-gray-600">
                  Status: <span className="font-semibold capitalize">{userData.subscriptionStatus}</span>
                </p>
                {safeFormatDate(userData.nextResetDate) && (
                  <p className="text-sm text-gray-600">
                    Next billing date: {safeFormatDate(userData.nextResetDate)}
                  </p>
                )}
              </div>
              <Button variant="secondary" onClick={handleManageBilling} loading={loading}>
                Manage Billing
              </Button>
            </div>
          </Card>
        )}

        {selectedPlan && !hasActiveSubscription && (
          <Alert
            type="info"
            message={`You've selected the ${selectedPlan.name} plan. Complete your subscription below.`}
          />
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {pricingPlans.map((plan) => (
            <Card
              key={plan.tierId}
              className={`relative ${
                plan.popular ? 'ring-2 ring-indigo-600 transform scale-105' : ''
              } ${isCurrentPlan(plan.tierId) ? 'opacity-75' : ''}`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {isCurrentPlan(plan.tierId) && (
                <div className="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-xs font-semibold">
                  Current Plan
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-indigo-600 mb-2">
                  ${plan.price}
                  <span className="text-xl font-normal text-gray-600">/mo</span>
                </div>
                <p className="text-gray-600 font-semibold">{plan.frequency}</p>
                <p className="text-sm text-gray-500 mt-2">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {isCurrentPlan(plan.tierId) ? (
                <Button fullWidth variant="ghost" disabled>
                  Current Plan
                </Button>
              ) : (
                <Button
                  fullWidth
                  loading={loading}
                  onClick={() => handleSubscribe(plan)}
                  disabled={loading}
                >
                  {hasActiveSubscription ? 'Switch Plan' : 'Subscribe Now'}
                </Button>
              )}
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">Subscription FAQ</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Card>
              <h3 className="font-semibold text-gray-900 mb-2">Can I change plans?</h3>
              <p className="text-sm text-gray-600">
                Yes, you can upgrade or downgrade anytime through the billing portal. Changes take effect at the next billing cycle.
              </p>
            </Card>
            <Card>
              <h3 className="font-semibold text-gray-900 mb-2">What payment methods are accepted?</h3>
              <p className="text-sm text-gray-600">
                We accept all major credit cards (Visa, Mastercard, American Express) through Stripe's secure payment platform.
              </p>
            </Card>
            <Card>
              <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-sm text-gray-600">
                Yes, cancel anytime with no fees. Your subscription remains active until the end of your billing period.
              </p>
            </Card>
            <Card>
              <h3 className="font-semibold text-gray-900 mb-2">Are there any hidden fees?</h3>
              <p className="text-sm text-gray-600">
                No hidden fees. The monthly price you see is what you pay. No setup fees, no cancellation fees.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
